/** Screen wake lock while navigating, reacquired on visibility return. */

let sentinel: WakeLockSentinel | null = null;
let wanted = false;
let acquiring = false;
let retryTimer: number | null = null;

function scheduleAcquire(): void {
  if (!wanted || document.visibilityState !== 'visible' || retryTimer !== null) return;
  retryTimer = window.setTimeout(() => {
    retryTimer = null;
    void acquire();
  }, 250);
}

async function acquire(): Promise<void> {
  if (
    !wanted ||
    document.visibilityState !== 'visible' ||
    !('wakeLock' in navigator) ||
    acquiring
  ) return;

  if (sentinel?.released) sentinel = null;
  if (sentinel) return;

  acquiring = true;
  try {
    const lock = await navigator.wakeLock.request('screen');
    if (!wanted || document.visibilityState !== 'visible') {
      void lock.release();
      return;
    }

    sentinel = lock;
    lock.addEventListener('release', () => {
      if (sentinel === lock) sentinel = null;
      // Android often fires visibility/focus before it reports the old lock
      // released. Re-acquire here as well so that ordering cannot lose it.
      scheduleAcquire();
    });
  } catch {
    // Denied (battery saver etc.) - non-fatal.
  } finally {
    acquiring = false;
  }
}

export function enableWakeLock(): void {
  wanted = true;
  void acquire();
}

export function disableWakeLock(): void {
  wanted = false;
  if (retryTimer !== null) window.clearTimeout(retryTimer);
  retryTimer = null;
  const lock = sentinel;
  sentinel = null;
  void lock?.release();
}

document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') scheduleAcquire();
});
window.addEventListener('pageshow', scheduleAcquire);
window.addEventListener('focus', scheduleAcquire);
